import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Card } from '../ui/Card'
import { SkeletonCard } from '../ui/Skeleton'
import { StockLevelBadge } from '../products/StockLevelBadge'
import { DEFAULT_LOW_STOCK_THRESHOLD, normalizeLowStockThreshold } from '@/lib/inventoryStock'
import * as productService from '../../services/productService'

type LowStockPreview = {
  id: number
  name: string
  stockQuantity: number
}

type DashboardLowStockProps = {
  threshold?: number
}

export function DashboardLowStock({ threshold = DEFAULT_LOW_STOCK_THRESHOLD }: DashboardLowStockProps) {
  const limit = normalizeLowStockThreshold(threshold)
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)
  const [items, setItems] = useState<LowStockPreview[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    productService
      .fetchProducts({ page: 1, limit: 5, lowStock: true })
      .then((res) => {
        if (cancelled) return
        setTotal(res.total)
        setItems(
          (res.items ?? [])
            .map((raw) => {
              if (!raw || typeof raw !== 'object') return null
              const o = raw as Record<string, unknown>
              const id = typeof o.id === 'number' ? o.id : Number(o.id)
              if (!Number.isFinite(id)) return null
              return {
                id,
                name: String(o.name ?? ''),
                stockQuantity:
                  typeof o.stockQuantity === 'number'
                    ? o.stockQuantity
                    : Number(o.stockQuantity ?? 0),
              }
            })
            .filter((r): r is LowStockPreview => r != null),
        )
      })
      .catch(() => {
        if (!cancelled) {
          setTotal(0)
          setItems([])
          setError('Could not load low-stock products')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [limit])

  if (loading) {
    return (
      <section aria-busy="true" aria-label="Loading low stock">
        <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Inventory</h3>
        <SkeletonCard />
      </section>
    )
  }

  if (error) {
    return (
      <Card className="p-5">
        <h3 className="text-sm font-semibold text-foreground">Low stock</h3>
        <p className="mt-2 text-sm text-destructive">{error}</p>
      </Card>
    )
  }

  if (total === 0) {
    return (
      <section>
        <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Inventory</h3>
        <Card className="p-5" padding={false}>
          <p className="text-sm font-medium text-foreground">Stock levels look healthy</p>
          <p className="mt-1 text-sm text-muted-foreground">
            No products at or below {limit} units. Adjust the threshold in Settings.
          </p>
        </Card>
      </section>
    )
  }

  return (
    <section>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Inventory</h3>
        <Link to="/products?lowStock=1" className="text-sm font-medium text-primary hover:underline">
          View all ({total})
        </Link>
      </div>
      <Card className="p-5" padding={false}>
        <p className="text-sm font-medium text-foreground">
          {total} product{total === 1 ? '' : 's'} at or below {limit} units
        </p>
        <ul className="mt-4 divide-y divide-border rounded-xl border border-border">
          {items.map((item) => (
            <li key={item.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm">
              <Link to={`/products/${item.id}`} className="font-medium text-primary hover:underline">
                {item.name}
              </Link>
              <div className="flex items-center gap-2">
                <span className="tabular-nums text-muted-foreground">{item.stockQuantity} left</span>
                <StockLevelBadge stockQuantity={item.stockQuantity} threshold={limit} />
              </div>
            </li>
          ))}
        </ul>
      </Card>
    </section>
  )
}
